// Payments/PaymentsPage.js

import React, { useState, useEffect } from 'react';
import axiosInstance from "../../../utils/axiosInstance";
import {
  Box, Typography, Tabs, Tab, Card, CardContent,
  useTheme, alpha, Stack, Skeleton
} from '@mui/material';
import BalanceDisplay from './BalanceDisplay';
import CustomerPaymentsTable from './CustomerPaymentsTable';
import WithdrawalHistoryTable from './WithdrawalHistoryTable';
import PaymentLinksPage from './PaymentLinksPage';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LinkIcon from '@mui/icons-material/Link';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DateRangeIcon from '@mui/icons-material/DateRange';
import EventNoteIcon from '@mui/icons-material/EventNote';

const FONT = "'Plus Jakarta Sans', sans-serif";

// ─── Metric Card ──────────────────────────────────────────────────────
const MetricCard = ({ label, value, icon: Icon, accentColor, loading }) => {
  const theme = useTheme();
  return (
    <Card
      elevation={0}
      sx={{
        flex: 1,
        minWidth: 0,
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        borderLeft: `4px solid ${accentColor}`,
        bgcolor: theme.palette.background.paper,
        transition: 'box-shadow 0.2s, transform 0.2s',
        '&:hover': {
          boxShadow: theme.shadows[2],
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography
              variant="caption"
              sx={{
                fontFamily: FONT,
                fontWeight: 600,
                color: theme.palette.text.secondary,
                textTransform: 'uppercase',
                letterSpacing: 0.8,
                fontSize: '0.68rem',
              }}
            >
              {label}
            </Typography>
            {loading ? (
              <Skeleton width={80} height={36} />
            ) : (
              <Typography
                variant="h5"
                sx={{
                  fontFamily: FONT,
                  fontWeight: 800,
                  color: theme.palette.text.primary,
                  mt: 0.5,
                  fontFeatureSettings: '"tnum"',
                }}
              >
                {value}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(accentColor, 0.08),
            }}
          >
            <Icon sx={{ fontSize: 22, color: accentColor }} />
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

// ─── Section Header ───────────────────────────────────────────────────
const SectionHeader = ({ icon: Icon, title }) => {
  const theme = useTheme();
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2.5 }}>
      <Icon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
      <Typography
        variant="subtitle1"
        sx={{ fontFamily: FONT, fontWeight: 700, color: theme.palette.text.primary }}
      >
        {title}
      </Typography>
    </Stack>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════
const PaymentsPage = () => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [cryptoConfig, setCryptoConfig] = useState(null);

  const handleRefresh = () => setRefreshKey(prev => prev + 1);

  useEffect(() => {
    axiosInstance.get('/pay/config')
      .then(res => setCryptoConfig(res.data.data))
      .catch(err => console.error('Failed to fetch crypto config', err));
  }, [refreshKey]);

  const tabs = [
    { label: 'Overview', icon: <DashboardIcon sx={{ fontSize: 18 }} /> },
    { label: 'Payment Links', icon: <LinkIcon sx={{ fontSize: 18 }} /> },
    { label: 'Transactions', icon: <ReceiptLongIcon sx={{ fontSize: 18 }} /> },
    { label: 'Withdrawals', icon: <AccountBalanceIcon sx={{ fontSize: 18 }} /> },
  ];

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 3 }, pt: 10, pb: 6 }}>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{ fontFamily: FONT, fontWeight: 800, color: theme.palette.text.primary, letterSpacing: -0.5 }}
        >
          Payments
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontFamily: FONT, color: theme.palette.text.secondary, mt: 0.5 }}
        >
          Manage revenue, payment links, and withdrawals
        </Typography>
      </Box>

      {/* Horizontal Tabs */}
      <Box sx={{ mb: 4, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          variant="scrollable"
          scrollButtons="auto"
          TabIndicatorProps={{
            sx: { height: 3, borderRadius: '3px 3px 0 0', bgcolor: theme.palette.primary.main },
          }}
          sx={{
            minHeight: 44,
            '& .MuiTab-root': {
              fontFamily: FONT,
              fontWeight: 600,
              fontSize: '0.85rem',
              textTransform: 'none',
              minHeight: 44,
              px: 2,
              mr: 1,
              color: theme.palette.text.secondary,
              '&:hover': { color: theme.palette.text.primary },
            },
            '& .MuiTab-root.Mui-selected': { color: theme.palette.primary.main },
          }}
        >
          {tabs.map((t, i) => (
            <Tab key={i} icon={t.icon} iconPosition="start" label={t.label} />
          ))}
        </Tabs>
      </Box>

      {/* Tab Content */}
      {tabValue === 0 && (
        <OverviewTab refreshKey={refreshKey} onRefresh={handleRefresh} cryptoConfig={cryptoConfig} />
      )}
      {tabValue === 1 && (
        <PaymentLinksPage refreshKey={refreshKey} cryptoConfig={cryptoConfig} />
      )}
      {tabValue === 2 && (
        <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, bgcolor: theme.palette.background.paper }}>
          <CardContent sx={{ p: 3 }}>
            <SectionHeader icon={ReceiptLongIcon} title="All Transactions" />
            <CustomerPaymentsTable refreshKey={refreshKey} />
          </CardContent>
        </Card>
      )}
      {tabValue === 3 && (
        <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, bgcolor: theme.palette.background.paper }}>
          <CardContent sx={{ p: 3 }}>
            <SectionHeader icon={AccountBalanceIcon} title="Withdrawal History" />
            <WithdrawalHistoryTable refreshKey={refreshKey} />
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// OVERVIEW TAB
// ═══════════════════════════════════════════════════════════════════════
const OverviewTab = ({ refreshKey, onRefresh, cryptoConfig }) => {
  const theme = useTheme();
  const [summary, setSummary] = useState({ last_week: 0, last_month: 0, last_year: 0 });
  const [loadingSummary, setLoadingSummary] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    setLoadingSummary(true);

    axiosInstance
      .get('/payments/summary', { signal: controller.signal })
      .then(res => { setSummary(res.data.summary); setLoadingSummary(false); })
      .catch(err => { if (err.name !== 'CanceledError') { console.error('Failed to fetch summary', err); setLoadingSummary(false); } });

    return () => controller.abort();
  }, [refreshKey]);

  return (
    <Stack spacing={3}>
      {/* Revenue Metrics */}
      <Box>
        <SectionHeader icon={TrendingUpIcon} title="Revenue Summary" />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <MetricCard label="This Week" value={`$${Number(summary.last_week).toFixed(2)}`} icon={CalendarTodayIcon} accentColor={theme.palette.primary.main} loading={loadingSummary} />
          <MetricCard label="This Month" value={`$${Number(summary.last_month).toFixed(2)}`} icon={DateRangeIcon} accentColor={theme.palette.secondary.main} loading={loadingSummary} />
          <MetricCard label="This Year" value={`$${Number(summary.last_year).toFixed(2)}`} icon={EventNoteIcon} accentColor={theme.palette.success.main} loading={loadingSummary} />
        </Stack>
      </Box>

      {/* Balance */}
      <BalanceDisplay onWithdrawalSuccess={onRefresh} refreshKey={refreshKey} />

      {/* Recent Payments */}
      <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, bgcolor: theme.palette.background.paper }}>
        <CardContent sx={{ p: 3 }}>
          <SectionHeader icon={ReceiptLongIcon} title="Recent Payments" />
          <CustomerPaymentsTable perPage={5} showPagination={false} refreshKey={refreshKey} />
        </CardContent>
      </Card>

      {/* Recent Withdrawals */}
      <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, bgcolor: theme.palette.background.paper }}>
        <CardContent sx={{ p: 3 }}>
          <SectionHeader icon={AccountBalanceIcon} title="Recent Withdrawals" />
          <WithdrawalHistoryTable perPage={5} showPagination={false} refreshKey={refreshKey} />
        </CardContent>
      </Card>
    </Stack>
  );
};

export default PaymentsPage;
